import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AgendaMomentInterface } from "../../../common/interfaces/AgendaMomentInterface";
import { increaseAgendaMomentLimit, toggleSortFast } from "../appointmentSlice";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import {
    CSSProperties,
    JSX,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { BarLoader } from "react-spinners";
import { useLazyGetAgendaMomentsQuery } from "../../apiData/apiDataSlice";
import {
    deleteDossier,
    deleteDossierId,
    setDossierLoading,
} from "../../../app/rootReducer";
import { setGeneralStep } from "../../helpType/helpTypeSlice";
import { GeneralStepsEnum } from "../../../common/constants/GeneralStepsEnum";
import { dossierTypeEnum } from "../../../common/constants/dossierTypeEnum";
import { fundingTypeEnum } from "../../../common/constants/fundingTypeEnum";
import { useParams } from "react-router-dom";
import { damageLocations } from "../../../common/constants/damageOptions";
import { scheduleScrollToStep } from "../../../common/helpers/scrollToStep";
import { DamageCauseEnum } from "../../../common/constants/DamageCauseEnum";
import { appointmentCopy } from "../appointment.copy";
import {
    filterEstablishmentsFromResponse,
    isEstablishmentSelectable,
    selectAgendaMoment,
} from "../helpers/establishmentSelection";

const override: CSSProperties = {
    display: "block",
    margin: "6rem auto",
    borderColor: "red",
};

interface ApiParams {
    dossierId: number;
    dossierAccessToken: string;
    lat?: number;
    long?: number;
    include_distances?: boolean;
    limit?: number;
    sort?: string;
    preferredEstablishmentId?: number;
    preferredEstablishmentChainId?: number;
    showOnlyPreferredEstablishment?: boolean;
    fundingType?: string;
}

const Establishment = (
    props: AgendaMomentInterface & { allowDeselect?: boolean },
): JSX.Element | null => {
    const { agendaMoments } = useAppSelector((state) => state.appointment);
    const { glassReplacement } = useAppSelector((state) => state.helpType);

    const { forceEstablishment } = useAppSelector(
        (state) => state.root.getParams,
    );
    const { fundingType, postMissingContractor } = useAppSelector(
        (state) => state.vehicleInformation,
    );
    const dispatch = useAppDispatch();

    let availability = null;
    const { dossier } = useAppSelector((state) => state.root);

    const rearWindowValue = damageLocations.find(
        (location) => location.label === "Achterruit",
    )?.value;
    const roofWindowValue = damageLocations.find(
        (location) => location.label === "Dakraam",
    )?.value;
    const sideWindowValue = damageLocations.find(
        (location) => location.label === "Zijruit",
    )?.value;
    const glassDamageLocation = Number(
        dossier?.glass_damage?.glass_damage_location,
    );

    if (
        fundingType !== fundingTypeEnum.Self &&
        props.availability?.dates &&
        typeof dossier?.appointment_type !== "string" &&
        dossier?.appointment_type?.code !== dossierTypeEnum.CaravanRepair
    ) {
        availability = props.availability.dates.find(
            (availabilityDate) => availabilityDate.has_capacity,
        );
    }

    let className = "c-locations__single u-fade";
    let showCross = false;
    if (
        agendaMoments?.activeAgendaMoment &&
        typeof dossier?.appointment_type !== "string" &&
        props.establishment.id ===
            agendaMoments.activeAgendaMoment.establishment.id
    ) {
        className += " isActive";
        showCross = props.allowDeselect !== false;
    }

    const handleClick = (handleClickProps: AgendaMomentInterface): void => {
        selectAgendaMoment(dispatch, handleClickProps, dossier, agendaMoments);
    };

    if (
        isEstablishmentSelectable(
            props,
            dossier,
            fundingType,
            glassDamageLocation,
            {
                rearWindowValue,
                roofWindowValue,
                sideWindowValue,
            },
        )
    ) {
        return (
            <button
                type="button"
                className={className}
                onClick={() => handleClick(props)}
            >
                <div
                    className={
                        "c-locations__single__info border-color-primary border-color-primary-hover"
                    }
                >
                    {showCross && (
                        <div className={"c-locations__single__info__cross"}>
                            <img src="/images/cross-black.svg" alt="cross" />
                        </div>
                    )}

                    <address>
                        <h5 className={"font-face-primary"}>
                            {props.establishment?.name}
                        </h5>
                        {!postMissingContractor &&
                            !glassReplacement &&
                            props.establishment.allows_appointments &&
                            availability?.date && (
                                <p>
                                    Eerst mogelijke optie:{" "}
                                    <u>
                                        {format(
                                            new Date(availability.date),
                                            "dd MMMM",
                                            {
                                                locale: nl,
                                            },
                                        )}
                                    </u>
                                    {props.availability
                                        ?.showExtendedWaitingTimes && (
                                        <strong>
                                            {" "}
                                            - Bij deze vestiging is het drukker
                                            dan normaal
                                        </strong>
                                    )}
                                </p>
                            )}
                        <span>
                            {props.establishment?.address?.street}{" "}
                            {props.establishment?.address?.house_number}
                            {props.establishment?.address
                                ?.house_number_addition && (
                                <>
                                    {" "}
                                    {
                                        props.establishment?.address
                                            ?.house_number_addition
                                    }
                                </>
                            )}
                            , {props.establishment?.address?.zip_code}{" "}
                            {props.establishment?.address?.city}
                        </span>
                    </address>

                    <div className={"c-locations__single__info__secondary"}>
                        {!forceEstablishment && props.distance?.distance && (
                            <div className="background-primary c-locations__single__info__secondary__badge">
                                {props.distance?.distance?.text}
                            </div>
                        )}
                    </div>
                </div>
            </button>
        );
    }

    return null;
};

const AppointmentEstablishments = (): JSX.Element | null => {
    const { agendaMoments, userLocation } = useAppSelector(
        (state) => state.appointment,
    );
    const { dossier, getParams, dossierIsDeleted } = useAppSelector(
        (state) => state.root,
    );
    const { fundingType, postMissingContractor, damageCause } = useAppSelector(
        (state) => state.vehicleInformation,
    );
    const { glassReplacement, activeHelpType } = useAppSelector(
        (state) => state.helpType,
    );
    const scrollRef = useRef<HTMLDivElement>(null);
    const isCaravanDamage = activeHelpType === dossierTypeEnum.CaravanRepair;

    const [trigger, { data: response }] = useLazyGetAgendaMomentsQuery();
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useAppDispatch();

    const glassDamageLocationValues = useMemo(() => {
        return {
            rearWindowValue: damageLocations.find(
                (location) => location.label === "Achterruit",
            )?.value,
            roofWindowValue: damageLocations.find(
                (location) => location.label === "Dakraam",
            )?.value,
            sideWindowValue: damageLocations.find(
                (location) => location.label === "Zijruit",
            )?.value,
        };
    }, []);

    const glassDamageLocation = Number(
        dossier?.glass_damage?.glass_damage_location,
    );

    const allFilteredEstablishments = useMemo(() => {
        if (!response?.data) {
            return [];
        }

        return filterEstablishmentsFromResponse(
            response.data,
            fundingType,
            isCaravanDamage,
            glassDamageLocation,
            glassDamageLocationValues,
        );
    }, [
        response?.data,
        fundingType,
        isCaravanDamage,
        glassDamageLocation,
        glassDamageLocationValues,
    ]);

    const selectableEstablishments = useMemo(
        () =>
            allFilteredEstablishments.filter((agendaMoment) =>
                isEstablishmentSelectable(
                    agendaMoment,
                    dossier,
                    fundingType,
                    glassDamageLocation,
                    glassDamageLocationValues,
                ),
            ),
        [
            allFilteredEstablishments,
            dossier,
            fundingType,
            glassDamageLocation,
            glassDamageLocationValues,
        ],
    );

    const filteredEstablishments = useMemo(() => {
        if (!allFilteredEstablishments.length) {
            return [];
        }

        if (agendaMoments.activeAgendaMoment?.establishment) {
            return allFilteredEstablishments
                .filter(
                    (agendaMoment) =>
                        agendaMoment.establishment.id ===
                        agendaMoments.activeAgendaMoment?.establishment.id,
                )
                .slice(0, agendaMoments.limit);
        }

        return allFilteredEstablishments.slice(0, agendaMoments.limit);
    }, [
        allFilteredEstablishments,
        agendaMoments.activeAgendaMoment,
        agendaMoments.limit,
    ]);

    useEffect(() => {
        if (isLoading) {
            dispatch(setDossierLoading(true));
            return;
        }

        dispatch(setDossierLoading(false));
    }, [isLoading, dispatch]);

    const showMoreClick = (): void => {
        dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
        dispatch(increaseAgendaMomentLimit());
    };

    useEffect(() => {
        if (dossier?.dossier_id && dossier?.dossier_access_token) {
            setIsLoading(true);
            const apiParams: ApiParams = {
                dossierId: dossier?.dossier_id,
                dossierAccessToken: dossier?.dossier_access_token,
                lat: userLocation?.lat,
                long: userLocation?.lng,
                include_distances: true,
                sort: agendaMoments.fast ? "date" : "distance",
                fundingType: fundingType,
                showOnlyPreferredEstablishment: getParams.forceEstablishment,
            };

            // Ensure required params when funding type is Self and 'fast' is selected
            if (fundingType === fundingTypeEnum.Self && agendaMoments.fast) {
                apiParams.sort = "date";
                apiParams.fundingType = fundingTypeEnum.Self;
            }

            if (getParams.establishmentId) {
                apiParams.preferredEstablishmentId = getParams.establishmentId;
            }

            if (!getParams.establishmentId && getParams.establishmentChainId) {
                apiParams.preferredEstablishmentChainId =
                    getParams.establishmentChainId;
            }

            trigger(apiParams).then(() => {
                setIsLoading(false);
            });
        }
    }, [
        userLocation?.lat,
        userLocation?.lng,
        agendaMoments.fast,
        dossier?.dossier_access_token,
        dossier?.dossier_id,
        fundingType,
        getParams.forceEstablishment,
        getParams.establishmentId,
        getParams.establishmentChainId,
        trigger,
    ]);

    useEffect(() => {
        if (
            isLoading ||
            !dossier ||
            agendaMoments.activeAgendaMoment ||
            selectableEstablishments.length !== 1
        ) {
            return;
        }

        selectAgendaMoment(
            dispatch,
            selectableEstablishments[0],
            dossier,
            agendaMoments,
        );
    }, [agendaMoments, dispatch, dossier, isLoading, selectableEstablishments]);

    const { slug } = useParams();

    useEffect(() => {
        const localDossierUuidKey = slug + "-dossier_uuid";
        const localDossierAccessTokenKey = slug + "-dossier_access_token";

        if (
            fundingType === fundingTypeEnum.Self &&
            agendaMoments.activeAgendaMoment &&
            !dossierIsDeleted
        ) {
            dispatch(
                deleteDossier({
                    dossierUuid: localStorage.getItem(localDossierUuidKey) + "",
                    fundingType: fundingTypeEnum.Self,
                }),
            ).then(() => {
                dispatch(deleteDossierId());
                localStorage.removeItem(localDossierUuidKey);
                localStorage.removeItem(localDossierAccessTokenKey);
            });
        }
    }, [
        agendaMoments.activeAgendaMoment,
        dispatch,
        dossierIsDeleted,
        fundingType,
        slug,
    ]);

    const showEstablishments =
        !!filteredEstablishments && !!dossier && !!response;

    useEffect(() => {
        if (!showEstablishments) return;
        scheduleScrollToStep(() => scrollRef.current);
    }, [showEstablishments]);

    if (filteredEstablishments && dossier) {
        return (
            <>
                <div
                    className="s-appointment__group border-color-primary"
                    ref={scrollRef}
                    id="establishment"
                >
                    {!isLoading && filteredEstablishments.length > 0 && (
                        <>
                            <h1 className="s-appointment__group__title">
                                {appointmentCopy.establishments.title}
                            </h1>

                            {!!activeHelpType &&
                                (activeHelpType ===
                                    dossierTypeEnum.BodyRepair ||
                                    activeHelpType ===
                                        dossierTypeEnum.CaravanRepair) && (
                                    <div className="c-form__row">
                                        <div className="c-form__group c-form__group--checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    value={agendaMoments.fast}
                                                    checked={
                                                        !!agendaMoments.fast
                                                    }
                                                    readOnly={true}
                                                    onClick={() =>
                                                        dispatch(
                                                            toggleSortFast(),
                                                        )
                                                    }
                                                />
                                                <p>
                                                    {
                                                        appointmentCopy
                                                            .establishments
                                                            .sortFast
                                                    }
                                                </p>
                                            </label>
                                        </div>
                                    </div>
                                )}
                        </>
                    )}

                    {!isLoading &&
                        !agendaMoments.activeAgendaMoment &&
                        filteredEstablishments.length === 0 && (
                            <div className="c-form__row">
                                <p>
                                    Helaas, er is geen beschikbare vestiging
                                    gevonden voor de ingevoerde gegevens.
                                </p>
                            </div>
                        )}

                    <BarLoader
                        color={"#E67A01"}
                        loading={isLoading}
                        cssOverride={override}
                    />

                    {!isLoading && filteredEstablishments.length > 0 && (
                        <>
                            <div className="c-locations u-fade">
                                {filteredEstablishments.map(
                                    (agendaMoment: AgendaMomentInterface) => {
                                        return (
                                            <Establishment
                                                {...agendaMoment}
                                                key={
                                                    agendaMoment.establishment
                                                        .id
                                                }
                                                allowDeselect={
                                                    selectableEstablishments.length >
                                                    1
                                                }
                                            />
                                        );
                                    },
                                )}
                            </div>

                            {!agendaMoments.activeAgendaMoment &&
                                response?.count &&
                                response.count > agendaMoments.limit && (
                                    <div className="c-form__buttons">
                                        <button
                                            type="button"
                                            onClick={() => showMoreClick()}
                                            className="c-form__buttons__single c-form__buttons__single___primary c-form__buttons__single___primary--full-width background-secondary"
                                        >
                                            <span className="font-face-primary font-color-white">
                                                Toon meer vestigingen
                                            </span>
                                        </button>
                                    </div>
                                )}
                        </>
                    )}
                </div>

                {fundingType === fundingTypeEnum.Self &&
                    agendaMoments.activeAgendaMoment && (
                        <div className="s-appointment__alert">
                            <h1 className="s-appointment__group__title">
                                Wanneer je zelf de schade wilt betalen kun je
                                alleen telefonisch een afspraak maken
                            </h1>
                            <p className="s-appointment__alert__text">
                                Neem contact op met{" "}
                                <b>
                                    {
                                        agendaMoments.activeAgendaMoment
                                            ?.establishment?.name
                                    }
                                </b>
                            </p>
                            <div className="c-form__buttons">
                                <a
                                    href={
                                        "tel:" +
                                        agendaMoments.activeAgendaMoment
                                            ?.establishment?.telephone[0]
                                    }
                                    className="c-form__buttons__single c-form__buttons__single___primary background-primary"
                                >
                                    <i
                                        className="icomoon-phone icomoon"
                                        style={{ color: "#1d2d58" }}
                                    />
                                    <span className="font-face-primary">
                                        {
                                            agendaMoments.activeAgendaMoment
                                                ?.establishment?.telephone[0]
                                        }
                                    </span>
                                </a>
                            </div>
                        </div>
                    )}

                {!glassReplacement &&
                    agendaMoments.activeAgendaMoment?.establishment?.name &&
                    !isCaravanDamage &&
                    postMissingContractor && (
                        <div className="s-appointment__alert">
                            <h1 className="s-appointment__group__title">
                                Voor deze reparatie kun je alleen telefonisch
                                een afspraak maken.
                            </h1>

                            <p className="s-appointment__alert__text">
                                Neem contact op met{" "}
                                <b>
                                    {
                                        agendaMoments.activeAgendaMoment
                                            ?.establishment.name
                                    }
                                </b>
                            </p>
                            <div className="c-form__buttons">
                                <a
                                    href={
                                        "tel:" +
                                        agendaMoments.activeAgendaMoment
                                            ?.establishment.telephone[0]
                                    }
                                    className="c-form__buttons__single c-form__buttons__single___primary background-primary"
                                >
                                    <i
                                        className="icomoon-phone icomoon"
                                        style={{ color: "#1d2d58" }}
                                    />
                                    <span className="font-face-primary">
                                        {
                                            agendaMoments.activeAgendaMoment
                                                ?.establishment.telephone[0]
                                        }
                                    </span>
                                </a>
                            </div>
                        </div>
                    )}

                {!glassReplacement &&
                    agendaMoments.activeAgendaMoment?.establishment?.name &&
                    !isCaravanDamage &&
                    damageCause.selected === DamageCauseEnum.HAIL && (
                        <div className="s-appointment__alert">
                            <h1 className="s-appointment__group__title">
                                Bij hagelschade is het niet mogelijk om een
                                digitale afspraak te maken.
                            </h1>

                            <p className="s-appointment__alert__text">
                                Neem contact op met{" "}
                                <b>
                                    {
                                        agendaMoments.activeAgendaMoment
                                            ?.establishment.name
                                    }
                                </b>
                            </p>
                            <div className="c-form__buttons">
                                <a
                                    href={
                                        "tel:" +
                                        agendaMoments.activeAgendaMoment
                                            ?.establishment.telephone[0]
                                    }
                                    className="c-form__buttons__single c-form__buttons__single___primary background-primary"
                                >
                                    <i
                                        className="icomoon-phone icomoon"
                                        style={{ color: "#1d2d58" }}
                                    />
                                    <span className="font-face-primary">
                                        {
                                            agendaMoments.activeAgendaMoment
                                                ?.establishment.telephone[0]
                                        }
                                    </span>
                                </a>
                            </div>
                        </div>
                    )}

                {glassReplacement &&
                    agendaMoments.activeAgendaMoment?.establishment?.name && (
                        <div className="s-appointment__alert">
                            {agendaMoments.activeAgendaMoment?.establishment
                                ?.name &&
                            agendaMoments.activeAgendaMoment?.establishment
                                ?.telephone?.[0] ? (
                                <>
                                    <h1 className="s-appointment__group__title">
                                        Voor deze ruit kun je alleen telefonisch
                                        een afspraak maken.
                                    </h1>

                                    <p className="s-appointment__alert__text">
                                        Neem contact op met{" "}
                                        <b>
                                            {
                                                agendaMoments.activeAgendaMoment
                                                    ?.establishment.name
                                            }
                                        </b>
                                    </p>
                                    <div className="c-form__buttons">
                                        <a
                                            href={
                                                "tel:" +
                                                agendaMoments.activeAgendaMoment
                                                    ?.establishment.telephone[0]
                                            }
                                            className="c-form__buttons__single c-form__buttons__single___primary background-primary"
                                        >
                                            <i
                                                className="icomoon-phone icomoon"
                                                style={{ color: "#1d2d58" }}
                                            />
                                            <span className="font-face-primary">
                                                {
                                                    agendaMoments
                                                        .activeAgendaMoment
                                                        ?.establishment
                                                        .telephone[0]
                                                }
                                            </span>
                                        </a>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h1 className="s-appointment__group__title">
                                        Voor deze ruit kun je alleen telefonisch
                                        een afspraak maken.
                                    </h1>
                                    <p className="s-appointment__alert__text">
                                        Neem contact op met{" "}
                                        <b>ABS autoherstel</b>
                                    </p>
                                    <div className="c-form__buttons">
                                        <a
                                            href={"tel:0900-6611111"}
                                            className="c-form__buttons__single c-form__buttons__single___primary background-primary"
                                        >
                                            <i
                                                className="icomoon-phone icomoon"
                                                style={{ color: "#1d2d58" }}
                                            />
                                            <span className="font-face-primary">
                                                0900-6611111
                                            </span>
                                        </a>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
            </>
        );
    }

    return null;
};

export default AppointmentEstablishments;
